import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { getCollectible, getCollectiblePrice, addToCart, lockCollectiblePrice, initiateCheckout, getPaymentConfig, createPaymentOrder, verifyPayment, cancelPaymentOrder } from '@/lib/api';
import { loadRazorpayCheckout } from '@/lib/razorpayCheckout';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingBag, CreditCard, Shield, Check, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { createProductSeo } from '@/lib/seoConfig';
import { formatSilverPurity } from '@/lib/productFormatting';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

export default function CollectibleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshCart } = useCart();
  const [collectible, setCollectible] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [priceError, setPriceError] = useState('');
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPriceDetail, setShowPriceDetail] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [liveRatesAvailable, setLiveRatesAvailable] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    Promise.all([
      getCollectible(id).then(r => r.data),
      getCollectiblePrice(id).then(r => r.data).catch(e => ({ __error: e.response?.data?.detail || 'Price confirmation required' })),
      getPaymentConfig().then(r => r.data).catch(() => ({ checkout_enabled: false, live_rates_available: false }))
    ]).then(([coll, price, paymentConfig]) => {
      setCollectible(coll);
      if (price?.__error) {
        setPriceBreakdown(null);
        setPriceError(price.__error);
      } else {
        setPriceBreakdown(price);
        setPriceError('');
      }
      setCheckoutEnabled(Boolean(paymentConfig?.checkout_enabled));
      setLiveRatesAvailable(Boolean(paymentConfig?.live_rates_available));
    }).catch(() => toast.error('Failed to load collectible'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = async () => {
    if (!user) { toast.error('Please sign in first'); return; }
    setAddingToCart(true);
    try {
      await addToCart(id);
      await refreshCart();
      toast.success('Added to your Chest');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) { toast.error('Please sign in first'); return; }
    setCheckoutLoading(true);
    try {
      await addToCart(id);
      await refreshCart();
      navigate('/chest');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail === 'Already in your chest') {
        await refreshCart();
        navigate('/chest');
      } else {
        toast.error(err.response?.data?.detail || 'Unable to open checkout');
      }
    } finally { setCheckoutLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-16 animate-pulse">
            <div className="aspect-square bg-secondary rounded-3xl" />
            <div className="space-y-4 pt-8">
              <div className="h-4 bg-secondary rounded w-1/4" />
              <div className="h-8 bg-secondary rounded w-3/4" />
              <div className="h-6 bg-secondary rounded w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!collectible) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-20 text-center">
          <h2 className="font-display text-2xl font-bold mb-4">Piece Not Found</h2>
          <Link to="/">
            <Button variant="outline" className="rounded-full">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = collectible.images?.length ? collectible.images : [collectible.image_url || 'https://images.unsplash.com/photo-1707569590646-2a51751bb9ef?w=800'];
  const pb = priceBreakdown;
  const displayedPrice = pb?.final_payable ?? null;
  const productSeo = createProductSeo(collectible, displayedPrice);

  return (
    <div className="min-h-screen bg-background" data-testid="collectible-detail-page">
      <Seo {...productSeo} />
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {/* Back */}
        <Link to={collectible.metal_type === 'gold' ? '/gold' : '/silver'} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to {collectible.metal_type === 'gold' ? 'Gold' : 'Silver'}
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {/* Images */}
          <div className="min-w-0 space-y-4">
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="aspect-square bg-secondary/30 rounded-3xl overflow-hidden"
            >
              <img
                src={images[selectedImage]}
                alt={collectible.name}
                className="w-full h-full object-cover"
                data-testid="main-product-image"
              />
            </motion.div>
            {images.length > 1 && (
              <div className="flex flex-col sm:flex-row gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 transition-colors duration-200 ${
                      i === selectedImage ? 'border-foreground' : 'border-transparent hover:border-border'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="min-w-0 flex flex-col"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
              {collectible.metal_type === 'gold' ? 'Gold' : 'Silver'} Collection
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight break-words mb-4" data-testid="product-name">
              {collectible.name}
            </h1>

            {/* Price */}
            <div className="mb-6">
              <p className={`${MONEY_CLASSNAME} text-3xl font-semibold text-gold`} data-testid="product-price">
                {formatInr(displayedPrice) || priceError || 'Live rate temporarily unavailable'}
              </p>
              <p className="text-sm text-muted-foreground mt-1" data-testid="product-pricing-rule">Confirmed total business costs + 13% profit target + GST</p>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {collectible.weight && (
                <div className="bg-secondary/50 rounded-xl p-3 text-center">
                  <p className="font-mono text-lg font-bold">{collectible.weight}g</p>
                  <p className="text-xs text-muted-foreground">Weight</p>
                </div>
              )}
              {collectible.purity_fineness && (
                <div className="bg-secondary/50 rounded-xl p-3 text-center">
                  <p className="font-mono text-lg font-bold" data-testid="product-purity">
                    {collectible.metal_type === 'silver'
                      ? formatSilverPurity(collectible)
                      : collectible.purity_fineness}
                  </p>
                  <p className="text-xs text-muted-foreground">Purity</p>
                </div>
              )}
              <div className="bg-secondary/50 rounded-xl p-3 text-center">
                <p className="font-mono text-lg font-bold flex items-center justify-center gap-1"><Shield className="w-4 h-4 text-green-400" /> BIS</p>
                <p className="text-xs text-muted-foreground">Certified</p>
              </div>
            </div>

            {/* Description */}
            {collectible.description && (
              <p className="text-muted-foreground leading-relaxed mb-8">
                {collectible.description}
              </p>
            )}

            {/* Price Transparency */}
            {pb && (
              <div className="mb-8" data-testid="price-breakdown">
                <button
                  onClick={() => setShowPriceDetail(!showPriceDetail)}
                  className="flex items-center justify-between w-full py-3 text-sm font-medium border-b border-border/50 hover:text-foreground transition-colors"
                  data-testid="toggle-price-detail"
                >
                  <span>Complete Price Breakdown</span>
                  {showPriceDetail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showPriceDetail && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="py-4 space-y-2.5 text-sm"
                  >
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Supplier Cost</span>
                      <span className={MONEY_CLASSNAME} data-testid="supplier-cost-value">{formatInr(pb.supplier_cost)}</span>
                    </div>
                    {pb.pricing_model === 'live_components' && pb.supplier_wastage_cost > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Supplier Wastage ({pb.supplier_wastage_percent}%)</span>
                        <span className={MONEY_CLASSNAME}>Included: {formatInr(pb.supplier_wastage_cost)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Packaging</span>
                      <span className={MONEY_CLASSNAME}>{formatInr(pb.packaging_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Insured Freight</span>
                      <span className={MONEY_CLASSNAME}>{formatInr(pb.shipping_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Allocated Overhead</span>
                      <span className={MONEY_CLASSNAME}>{formatInr(pb.allocated_overhead_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Separate delivery charge</span>
                      <span className={MONEY_CLASSNAME}>{formatInr(pb.customer_delivery_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment Cost</span>
                      <span className={MONEY_CLASSNAME}>{formatInr(pb.gateway_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">13% cost markup</span>
                      <span className={MONEY_CLASSNAME} data-testid="sohm-margin-value">{formatInr(pb.projected_profit ?? pb.profit_13_percent)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>GST ({pb.gst_percent}%)</span>
                      <span className={MONEY_CLASSNAME} data-testid="gst-value">{formatInr(pb.gst)}</span>
                    </div>
                    <Separator className="!my-2" />
                    <div className="flex justify-between font-bold text-base">
                      <span>Total</span>
                      <span className={`${MONEY_CLASSNAME} text-gold`}>{formatInr(pb.final_payable)}</span>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || collectible.quantity_remaining <= 0 || !pb}
                variant="outline"
                className="flex-1 rounded-full h-12 text-sm font-semibold uppercase tracking-wide border-border hover:bg-secondary"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                {addingToCart ? 'Adding...' : 'Add to Chest'}
              </Button>
              <Button
                onClick={handleBuyNow}
                disabled={checkoutLoading || collectible.quantity_remaining <= 0 || !checkoutEnabled || !pb}
                className="flex-1 rounded-full h-12 text-sm font-semibold uppercase tracking-wide bg-foreground text-background hover:bg-foreground/90"
                data-testid="buy-now-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {checkoutLoading ? 'Processing...' : checkoutEnabled ? 'Buy Now' : liveRatesAvailable ? 'Checkout Unavailable' : 'Live Rate Unavailable'}
              </Button>
            </div>

            {!liveRatesAvailable && <p className="text-center text-sm text-amber-400 mt-3" data-testid="product-rate-unavailable">Live rate temporarily unavailable</p>}
            {checkoutError && <p className="text-center text-sm text-red-400 mt-3" role="alert" data-testid="product-checkout-error">{checkoutError}</p>}

            {collectible.quantity_remaining <= 0 && (
              <p className="text-center text-sm text-destructive mt-3" data-testid="sold-out-msg">Sold Out</p>
            )}

            {/* Trust badges */}
            <div className="flex items-center justify-center gap-6 mt-8 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-400" /> BIS Certified</span>
              <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-green-400" /> Secure Payment</span>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
